// Eskom PARCC — full reviewed working-paper DOCUMENT export (pure).
//
// Where buildComplianceWorkbook emits a flat per-criterion table, this builder
// renders each reviewed transaction as a complete working-paper DOCUMENT block —
// header, review block, the full checklist table, then findings — and stacks the
// blocks one per worksheet per procurement route. It exists because the flat
// export gave reviewers "only parts": no review metadata, and a legacy paper's
// checklist arrived as one unreadable text-blob cell.
//
// Checklist source: normalised CES rows when present; otherwise the legacy blob
// (updatedFindings → workingPaperNotes → initialFindings) parsed back into rows
// via parseChecklistBlob, with trailing prose kept as Commentary. Review metadata
// reads from the WorkingPaper, falling back to the transaction. Returns the same
// `ComplianceSheet` { sheetName, rows } intermediates the XLSX glue consumes — so
// the row/column logic unit-tests as data, no binary involved.
import type { Transaction } from '../../data/types';
import type { WorkingPaperRead, CesRowRead, FindingRead } from '../../lib/db';
import { type ComplianceSheet, type CesRowsByRef, routeOf, cleanSheetName } from './buildComplianceWorkbook';
import type { WorkingPapersByRef } from './buildWorkingPaperSheet';
import { parseChecklistBlob } from './parseChecklistBlob';
import { neutraliseCell } from './neutraliseCell';
import { genuineFindings } from '../../domain/findings';

/** Map from a transaction `referenceNo` to its findings (the join). */
export type FindingsByRef = ReadonlyMap<string, FindingRead[]>;

/** The checklist table's column headers (CES-row order; also used for blob rows). */
export const WORKING_PAPER_CHECKLIST_HEADERS = [
  'No',
  'Stage',
  'Sub-Criteria',
  'Result',
  'Finding',
] as const;

/** The findings table's column headers. */
const FINDINGS_HEADERS = ['#', 'Finding', 'Suggested', 'Final', 'Rationale', 'Status'] as const;

/** Coerce any cell value to a trimmed string ('' for null/undefined). */
function s(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

/** First non-empty of the candidates (already-trimmed), else ''. */
function firstOf(...candidates: (string | undefined)[]): string {
  for (const c of candidates) {
    const t = s(c);
    if (t) return t;
  }
  return '';
}

/** A resolved checklist: structured rows + commentary + which source produced it. */
interface ResolvedChecklist {
  rows: string[][];
  commentary: string;
  /** The raw blob text consumed (when source === 'blob'), so callers can suppress re-dumping it. */
  blobText: string;
  source: 'ces' | 'blob' | 'none';
}

/** Checklist row from a normalised CES row. */
function cesChecklistRow(r: CesRowRead): string[] {
  return [s(r.seq), s(r.stage), s(r.criterion), s(r.status), s(r.finding)];
}

/**
 * Resolve a transaction's checklist: prefer normalised CES rows; else parse the
 * first legacy blob (updatedFindings → workingPaperNotes → initialFindings) that
 * yields ≥1 item; else none. Blob-sourced rows have no Stage (the criterion
 * carries the sub-code), so that column stays blank. Any legacy "Action:" text the
 * blob still carries is folded into the Finding cell so it is not silently lost.
 */
function resolveChecklist(tx: Transaction, cesRows: CesRowRead[] | undefined): ResolvedChecklist {
  if (cesRows && cesRows.length > 0) {
    return { rows: cesRows.map(cesChecklistRow), commentary: '', blobText: '', source: 'ces' };
  }
  for (const candidate of [tx.updatedFindings, tx.workingPaperNotes, tx.initialFindings]) {
    const blobText = s(candidate);
    if (!blobText) continue;
    const parsed = parseChecklistBlob(blobText);
    if (parsed.items.length > 0) {
      const rows = parsed.items.map((i) => [
        i.no,
        '',
        i.criterion,
        i.result,
        i.action ? `${i.finding}${i.finding ? '\n' : ''}Action: ${i.action}` : i.finding,
      ]);
      return { rows, commentary: parsed.commentary, blobText, source: 'blob' };
    }
  }
  return { rows: [], commentary: '', blobText: '', source: 'none' };
}

/** Append a "Label | value" row only when the value is non-empty. */
function kv(block: string[][], label: string, value: string): void {
  if (value) block.push([label, value]);
}

/** Build the document block (array-of-arrays) for one reviewed transaction. */
function paperBlock(
  tx: Transaction,
  paper: WorkingPaperRead | undefined,
  cesRows: CesRowRead[] | undefined,
  findings: FindingRead[] | undefined,
): string[][] {
  const ref = s(tx.referenceNo);
  const supplier = s(tx.supplier);
  const checklist = resolveChecklist(tx, cesRows);
  const block: string[][] = [];

  // ── Header band + identity ────────────────────────────────────────────────
  block.push([`══ WORKING PAPER — ${ref}${supplier ? ` — ${supplier}` : ''} ══`]);
  kv(block, 'Reference No', ref);
  kv(block, 'Supplier', supplier);
  kv(block, 'Division', s(tx.division));
  kv(block, 'Procurement Route', routeOf(tx));
  kv(block, 'Value', firstOf(tx.valueDisplay, tx.value ? String(tx.value) : ''));
  kv(block, 'CSD Number', s(tx.csdNumber));
  kv(block, 'Dataset', s(tx.dataset));
  kv(block, 'Year', s(tx.year));
  kv(block, 'Period', s(tx.period));
  kv(block, 'Gaps Status', s(tx.gapsStatus));
  kv(block, 'Link', s(tx.link));

  // ── Review block (working paper first, transaction as fallback) ───────────
  block.push(['Review']);
  kv(block, 'Review Status', firstOf(paper?.reviewStatus as string, tx.reviewStatus));
  // "Reviewer" is the ACTUAL reviewer's NAME (owner feedback #8); the coordinator
  // who captured on their behalf is shown as "Captured By" (their email). Falls
  // back to the reviewer email when no name was recorded.
  kv(block, 'Reviewer', firstOf(paper?.reviewerName, paper?.reviewerEmail, tx.reviewerEmail));
  kv(block, 'Reviewed At', firstOf(paper?.reviewedAt, tx.reviewedAt));
  kv(block, 'Compliance Level', firstOf(tx.complianceLevelOverride as string, paper?.complianceLevel as string, tx.complianceLevel as string));
  kv(block, 'Captured By', firstOf(paper?.capturedByEmail, tx.capturedByEmail));
  kv(block, 'Captured Date', firstOf(paper?.capturedDate, tx.capturedDate));
  kv(block, 'Progress', firstOf(paper?.progress, tx.progress));
  kv(block, 'Finding Text', firstOf(paper?.findingText, tx.findingText));
  // Working Paper Notes — but never re-dump the blob we just rendered as the checklist.
  const notes = firstOf(paper?.workingPaperNotes, tx.workingPaperNotes);
  if (notes && notes !== checklist.blobText) kv(block, 'Working Paper Notes', notes);
  // When no checklist could be resolved, surface the legacy free-text so nothing is lost.
  if (checklist.source === 'none') {
    kv(block, 'Updated Findings', s(tx.updatedFindings));
    kv(block, 'Initial Findings', s(tx.initialFindings));
  }

  // ── Checklist table ───────────────────────────────────────────────────────
  if (checklist.rows.length > 0) {
    block.push(['Checklist']);
    block.push([...WORKING_PAPER_CHECKLIST_HEADERS]);
    block.push(...checklist.rows);
    kv(block, 'Commentary', checklist.commentary);
  }

  // ── Findings table (only when present) ────────────────────────────────────
  if (findings && findings.length > 0) {
    block.push(['Findings']);
    block.push([...FINDINGS_HEADERS]);
    findings.forEach((f, i) => {
      block.push([
        String(i + 1),
        s(f.text),
        s(f.suggestedCompliance),
        s(f.finalCompliance),
        s(f.rationale),
        firstOf(f.effectiveStatus, f.legacyStatus),
      ]);
    });
  }

  block.push([]); // blank separator before the next paper
  // Defence-in-depth: neutralise any free-text cell a spreadsheet could read as a formula.
  return block.map((row) => row.map(neutraliseCell));
}

/**
 * Build the reviewed working-paper document workbook: one worksheet per
 * procurement route (routes ordered by name, sheet names Excel-safe + unique),
 * each a stack of per-paper document blocks in input order. Pass the ALREADY
 * reviewed-selected transactions (see selectReviewedTransactions). Pure — no XLSX,
 * no DOM.
 */
export function buildWorkingPaperDocumentSheets(
  reviewedTransactions: readonly Transaction[],
  workingPapersByRef: WorkingPapersByRef,
  cesRowsByRef: CesRowsByRef,
  findingsByRef: FindingsByRef,
): ComplianceSheet[] {
  if (reviewedTransactions.length === 0) return [];

  const byRoute = new Map<string, string[][]>();
  for (const tx of reviewedTransactions) {
    const route = routeOf(tx);
    const block = paperBlock(
      tx,
      workingPapersByRef.get(tx.referenceNo),
      cesRowsByRef.get(tx.referenceNo),
      // Genuine findings only — one per criterion, no "Additional comments" notes
      // (owner feedback Aug 2026: raw rows inflated the Findings table count).
      genuineFindings(findingsByRef.get(tx.referenceNo) ?? []),
    );
    const bucket = byRoute.get(route);
    if (bucket) bucket.push(...block);
    else byRoute.set(route, [...block]);
  }

  const used = new Set<string>();
  return [...byRoute.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([route, rows]) => ({ sheetName: cleanSheetName(route, used), rows }));
}
